import React, { useEffect, useState } from "react";
import { IconCheck, IconCopy } from "@tabler/icons-react";

const formatLanguageLabel = (language) =>
  String(language || "").replace(/[-_]/g, " ").trim().toUpperCase();

const normalizeCode = (value) => {
  const raw = String(value || "").replace(/\r\n?/g, "\n");
  const trimmed = raw.replace(/^\n+|\n+$/g, "");
  const lines = trimmed.split("\n");
  const nonEmptyLines = lines.filter((line) => line.trim().length > 0);

  if (nonEmptyLines.length === 0) return "";

  const minIndent = nonEmptyLines.reduce((smallest, line) => {
    const match = line.match(/^[ \t]*/);
    const indent = match ? match[0].length : 0;
    return Math.min(smallest, indent);
  }, Number.POSITIVE_INFINITY);

  return lines
    .map((line) => line.slice(Math.min(minIndent, line.length)).replace(/[ \t]+$/g, ""))
    .join("\n");
};

const PostsCodeBlock = ({ language, code }) => {
  const [copyState, setCopyState] = useState("idle");
  const safeCode = normalizeCode(code);
  const nonEmptyLines = safeCode
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const isSingleLine = nonEmptyLines.length <= 1 && !safeCode.includes("\n");
  const isCompactLanguage = String(language || "").toLowerCase() === "flow";
  const isCompactBlock =
    !isSingleLine && nonEmptyLines.length > 0 && (isCompactLanguage || nonEmptyLines.length <= 6);

  useEffect(() => {
    if (copyState !== "copied") return undefined;
    const timeout = window.setTimeout(() => setCopyState("idle"), 1600);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(safeCode);
      setCopyState("copied");
    } catch {
      setCopyState("idle");
    }
  };

  return (
    <div className="mx-2 not-prose my-2.5 overflow-hidden rounded-xl border border-slate-300/70 bg-white/70 text-slate-900 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
      <div
        className={[
          "flex items-center justify-between gap-3 px-[8.5px]",
          isCompactBlock ? "min-h-8 py-0.5" : "min-h-9 py-1",
        ].join(" ")}
      >
        <div className="mx-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600">
          {formatLanguageLabel(language)}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-600"
        >
          <span>{copyState === "copied" ? "Copied" : "Copy"}</span>
          {copyState === "copied" ? (
            <IconCheck size={14} stroke={1.8} aria-hidden="true" className="shrink-0" />
          ) : (
            <IconCopy size={14} stroke={1.8} aria-hidden="true" className="shrink-0" />
          )}
        </button>
      </div>
      <div className="mx-[9px] border-b border-slate-300/70" />
      <pre
        className={[
          "m-0 overflow-y-auto bg-transparent px-[6px] text-[12.5px] text-slate-900",
          isCompactBlock ? "max-h-[180px] leading-[1.5] !pt-0 !pb-0.5" : "max-h-[180px] leading-[1.45]",
          isSingleLine ? "!py-0.5" : isCompactBlock ? "!pt-0 !pb-0.5" : "!py-0.5",
        ].join(" ")}
      >
        <div className="mt-2 mb-1 mx-[13px]">
          <code className="m-0 block whitespace-pre px-0 pt-0 pb-1.5 font-mono">
            {safeCode}
          </code>
        </div>
      </pre>
    </div>
  );
};

export default PostsCodeBlock;
